/**
 * Self-Healing Orchestrator - Coordinates the full detect → plan → (propose | repair) cycle.
 *
 * GOVERNANCE (Phase 2-2.5 Containment):
 *   When SELF_HEALING_EXECUTION_ENABLED = false (current state):
 *     - Issues are detected (runtimeMonitor)
 *     - Repair plans are generated (fixPlanner)
 *     - Proposals are emitted to eventBus (selfHealing:repair_proposed)
 *     - Proposals are recorded in ImmutableHistory (healing:proposal)
 *     - NO store mutations occur
 *     - NO AI escalation writes occur
 *
 *   When SELF_HEALING_EXECUTION_ENABLED = true (re-authorized, future):
 *     - Original execution path activates
 *     - All mutations route through authorized Tier 2 paths (Phase 2-2.5 scope)
 *
 * Server-safe: no direct Zustand dependency.
 */

import { runRuntimeMonitor, type DetectedIssue } from "@/ai/selfHealing/runtimeMonitor";
import { generateFixPlan, type FixPlan }          from "@/ai/selfHealing/fixPlanner";
import { executeRepair }                           from "@/ai/selfHealing/repairEngine";
import { fixBuildErrors }                          from "@/ai/selfHealing/aiFixer";
import { recordFixResult, getLearnedFix }          from "@/ai/selfHealing/learningEngine";
import { SELF_HEALING_EXECUTION_ENABLED,
         SUSPENSION_REASON }                       from "@/ai/selfHealing/governanceConfig";
import { eventBus }                               from "@/runtime/eventBus";
import { immutableHistory }                       from "@/core/history/immutableHistory";
import { logger }                                 from "@/system/logger";

/* =========================================================
   SERVER-SAFE PROJECT STATE
   ========================================================= */

interface OrchestratorStoreState {
  files:           any[];
  updateFiles:     (f: any[]) => void;
  getSnapshot:     () => any;
  restoreSnapshot: (snap: any) => void;
  setDirty?:       (d: boolean) => void;
}

function getProjectState(): OrchestratorStoreState {
  if (typeof window !== "undefined") {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { useProjectStore } = require("@/store/projectStore");
      return useProjectStore.getState() as OrchestratorStoreState;
    } catch { /* fall through */ }
  }
  return {
    files:           [],
    updateFiles:     () => {},
    getSnapshot:     () => null,
    restoreSnapshot: () => {},
    setDirty:        () => {},
  };
}

/* =========================================================
   CONFIG
   ========================================================= */

const MAX_AUTO_FIX       = 3;
const FAILURE_THRESHOLD  = 5;
const MAX_EXECUTION_TIME = 15_000;
const CYCLE_TIMEOUT      = 120_000;

/* =========================================================
   TYPES
   ========================================================= */

export type HealingStatus =
  | "resolved"
  | "ai_resolved"
  | "failed"
  | "skipped"
  | "blocked"
  | "no_issues"
  /** Execution suspended - repair was proposed but not applied. */
  | "proposed";

export interface IssueHealingResult {
  issueId:    string;
  title:      string;
  severity:   string;
  status:     HealingStatus;
  attempts:   number;
  durationMs: number;
  error?:     string;
}

export interface SelfHealingCycleReport {
  totalIssues:        number;
  resolved:           number;
  aiResolved:         number;
  failed:             number;
  skipped:            number;
  blocked:            number;
  /** Issues for which proposals were generated but execution was suspended. */
  proposed:           number;
  healthScore:        number;
  results:            IssueHealingResult[];
  durationMs:         number;
  completedAt:        number;
  /** true while SELF_HEALING_EXECUTION_ENABLED = false. */
  executionSuspended: boolean;
}

/* =========================================================
   MEMORY - LIGHTWEIGHT LEARNING LAYER
   ========================================================= */

const failureMap: Record<string, number> = {};
const successMap: Record<string, number> = {};
const blockedSet = new Set<string>();

/* =========================================================
   HELPERS
   ========================================================= */

function prioritizeIssues(issues: DetectedIssue[]): DetectedIssue[] {
  const weight: Record<string, number> = {
    critical: 4,
    major:    3,
    minor:    2,
    info:     0,
  };
  return [...issues].sort(
    (a, b) => (weight[b?.severity] ?? 0) - (weight[a?.severity] ?? 0)
  );
}

function shouldBlockIssue(issueId: string): boolean {
  return (
    blockedSet.has(issueId) ||
    (failureMap[issueId] ?? 0) >= FAILURE_THRESHOLD
  );
}

function recordFailure(issueId: string): void {
  failureMap[issueId] = (failureMap[issueId] ?? 0) + 1;
  if (failureMap[issueId] >= FAILURE_THRESHOLD) {
    blockedSet.add(issueId);
    logger.warn(`[Orchestrator] Issue ${issueId} blocked after ${FAILURE_THRESHOLD} failures`);
  }
}

function recordSuccess(issueId: string): void {
  successMap[issueId] = (successMap[issueId] ?? 0) + 1;
  failureMap[issueId] = 0;
  blockedSet.delete(issueId);
}

function computeHealthScore(results: IssueHealingResult[]): number {
  if (results.length === 0) return 100;
  const resolved = results.filter(
    r => r.status === "resolved" || r.status === "ai_resolved"
  ).length;
  return Math.round((resolved / results.length) * 100);
}

async function safeExecute<T>(fn: () => Promise<T> | T): Promise<T | null> {
  try {
    return await Promise.resolve(fn());
  } catch (err) {
    logger.error("[Orchestrator] Safe execution failed:", err);
    return null;
  }
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeout: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(
      () => reject(new Error(`Timeout after ${timeoutMs}ms`)),
      timeoutMs,
    );
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeout!);
  }
}

function validateFixedFiles(files: any[]): boolean {
  if (!Array.isArray(files)) return false;
  return files.every(
    f =>
      f &&
      typeof f.path === "string" &&
      f.path.length > 0 &&
      typeof f.content === "string"
  );
}

/* =========================================================
   PROPOSAL MODE
   Called when SELF_HEALING_EXECUTION_ENABLED = false.
   Generates the repair plan, emits events, records in history.
   No store mutations occur.
   ========================================================= */

async function proposeRepair(issue: DetectedIssue): Promise<IssueHealingResult> {
  const startTime = Date.now();

  if (shouldBlockIssue(issue.id)) {
    logger.warn(`[Orchestrator] Blocked proposal for "${issue.title}"`);
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "blocked",
      attempts:  0,
      durationMs: Date.now() - startTime,
    };
  }

  if (!issue.autoFixable) {
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "skipped",
      attempts:  0,
      durationMs: Date.now() - startTime,
    };
  }

  // Generate the repair plan (pure function - no side effects)
  const plan: FixPlan | null =
    (await safeExecute(() => getLearnedFix(issue))) ??
    (await safeExecute(() => generateFixPlan(issue)));

  if (!plan?.steps?.length) {
    logger.warn(`[Orchestrator] No fix plan generated for "${issue.title}"`);
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "failed",
      attempts:  0,
      durationMs: Date.now() - startTime,
      error:     "No valid fix plan generated",
    };
  }

  // Emit the repair proposal - execution is blocked, proposal is observable
  eventBus.emit({
    type:             "selfHealing:repair_proposed",
    issueId:          issue.id,
    category:         issue.category,
    severity:         issue.severity,
    title:            issue.title,
    planSteps:        plan.steps.length,
    estimatedImpact:  plan.estimatedImpact,
    requiresReview:   plan.requiresReview,
    executionBlocked: true,
  });

  // Record in ImmutableHistory - proposals are audit-logged even when not applied.
  // Both fingerprints are "" because no schema mutation occurred.
  immutableHistory.record(
    "healing:proposal",
    "",
    "",
    {
      issueId:         issue.id,
      category:        issue.category,
      severity:        issue.severity,
      title:           issue.title,
      planSteps:       plan.steps.length,
      estimatedImpact: plan.estimatedImpact,
      requiresReview:  plan.requiresReview,
      autoFixable:     issue.autoFixable,
    },
    "system",
    `Self-healing proposal (execution suspended): ${issue.title}`,
  );

  // Emit per-issue execution blocked notification
  eventBus.emit({
    type:   "selfHealing:execution_blocked",
    reason: SUSPENSION_REASON,
  });

  logger.info(
    `[Orchestrator] Proposal recorded for "${issue.title}" - ${plan.steps.length} steps proposed, execution suspended`
  );

  return {
    issueId:   issue.id,
    title:     issue.title,
    severity:  issue.severity,
    status:    "proposed",
    attempts:  0,
    durationMs: Date.now() - startTime,
  };
}

/* =========================================================
   EXECUTION MODE (FUTURE - requires SELF_HEALING_EXECUTION_ENABLED = true)
   Requires: projectStore canonicalization, schema bridge integration,
   Tier 2 authorized mutation routing, schema validators.
   ========================================================= */

async function healIssue(
  issue: DetectedIssue,
  store: OrchestratorStoreState,
): Promise<IssueHealingResult> {
  const startTime = Date.now();
  let attempts    = 0;

  // ── GOVERNANCE GATE (double-lock defense) ─────────────────────────────────
  // Primary gate is in runSelfHealingCycle(). This double-lock prevents
  // execution if healIssue() is ever called directly (e.g., healSpecificIssue).
  if (!SELF_HEALING_EXECUTION_ENABLED) {
    return proposeRepair(issue);
  }

  if (shouldBlockIssue(issue.id)) {
    logger.error(`[Orchestrator] Blocked: "${issue.title}" (too many prior failures)`);
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "blocked",
      attempts:  0,
      durationMs: Date.now() - startTime,
    };
  }

  if (!issue.autoFixable) {
    logger.warn(`[Orchestrator] Skipping "${issue.title}" - requires manual fix`);
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "skipped",
      attempts:  0,
      durationMs: Date.now() - startTime,
    };
  }

  const snapshot = store.getSnapshot();

  const plan: FixPlan | null =
    (await safeExecute(() => getLearnedFix(issue))) ??
    (await safeExecute(() => generateFixPlan(issue)));

  if (!plan?.steps?.length) {
    logger.warn(`[Orchestrator] No valid fix plan for "${issue.title}"`);
    recordFailure(issue.id);
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "failed",
      attempts:  0,
      durationMs: Date.now() - startTime,
      error:     "No valid fix plan generated",
    };
  }

  logger.info(`[Orchestrator] Plan: ${plan.steps.length} steps for "${issue.title}"`);

  attempts++;
  const result = await safeExecute(() =>
    withTimeout(executeRepair(issue, plan), MAX_EXECUTION_TIME)
  );

  if (!result?.applied) {
    logger.warn(`[Orchestrator] Repair failed: "${issue.title}"`);
    recordFailure(issue.id);
    // Phase 3C: repairEngine handles canonical rollback internally (result.rolledBack = true).
    // Only restore flat snapshot here if repairEngine did NOT already roll back.
    if (!result?.rolledBack) {
      store.restoreSnapshot(snapshot);
    }
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "failed",
      attempts,
      durationMs: Date.now() - startTime,
      error:     result?.failedStep ?? "Repair not applied",
    };
  }

  const postIssues  = await safeExecute(() => runRuntimeMonitor());
  const stillBroken = postIssues?.find(i => i.id === issue.id);

  if (!stillBroken) {
    logger.info(`[Orchestrator] Resolved: "${issue.title}"`);
    recordSuccess(issue.id);
    await safeExecute(() => recordFixResult(issue, plan, "success"));
    return {
      issueId:   issue.id,
      title:     issue.title,
      severity:  issue.severity,
      status:    "resolved",
      attempts,
      durationMs: Date.now() - startTime,
    };
  }

  logger.warn(`[Orchestrator] Still broken → AI escalation: "${issue.title}"`);

  for (let attempt = 1; attempt <= MAX_AUTO_FIX; attempt++) {
    attempts++;
    try {
      const files = store.files || [];
      const contextError = [
        `Issue: ${stillBroken.title}`,
        `Description: ${stillBroken.description}`,
        `Root Cause: ${stillBroken.rootCause}`,
        `Suggested Fix: ${stillBroken.suggestedFix}`,
      ].join("\n");

      const fixedFiles = await withTimeout(
        fixBuildErrors(files, contextError),
        MAX_EXECUTION_TIME,
      );

      if (!validateFixedFiles(fixedFiles)) {
        throw new Error("Invalid AI fix output - failed file validation");
      }

      store.updateFiles(fixedFiles);
      logger.info(`[Orchestrator] AI fix applied (attempt ${attempt}/${MAX_AUTO_FIX})`);

      const recheck  = await safeExecute(() => runRuntimeMonitor());
      const resolved = !recheck?.find(i => i.id === issue.id);

      if (resolved) {
        logger.info(`[Orchestrator] AI resolved: "${issue.title}"`);
        recordSuccess(issue.id);
        await safeExecute(() => recordFixResult(issue, plan, "success"));
        return {
          issueId:   issue.id,
          title:     issue.title,
          severity:  issue.severity,
          status:    "ai_resolved",
          attempts,
          durationMs: Date.now() - startTime,
        };
      }

      if (attempt === MAX_AUTO_FIX) {
        throw new Error(`Max AI attempts (${MAX_AUTO_FIX}) reached without resolution`);
      }

    } catch (err: any) {
      logger.error(`[Orchestrator] AI attempt ${attempt} failed:`, err?.message);
      recordFailure(issue.id);
      await safeExecute(() => recordFixResult(issue, plan, "failure"));
      store.restoreSnapshot(snapshot);
      logger.warn("[Orchestrator] Rolled back to safe snapshot");
      return {
        issueId:   issue.id,
        title:     issue.title,
        severity:  issue.severity,
        status:    "failed",
        attempts,
        durationMs: Date.now() - startTime,
        error:     err?.message,
      };
    }
  }

  return {
    issueId:   issue.id,
    title:     issue.title,
    severity:  issue.severity,
    status:    "failed",
    attempts,
    durationMs: Date.now() - startTime,
    error:     "Exhausted all healing strategies",
  };
}

/* =========================================================
   MAIN SELF-HEAL CYCLE
   ========================================================= */

export async function runSelfHealingCycle(): Promise<SelfHealingCycleReport> {
  const cycleStart = Date.now();
  const store      = getProjectState();

  logger.debug("[Orchestrator] Starting self-healing cycle");

  const issues = await safeExecute(() => runRuntimeMonitor());

  const actionableIssues = (issues ?? []).filter(i => i.severity !== "info");

  // ── Emit cycle start ────────────────────────────────────────────────────
  eventBus.emit({
    type:             "selfHealing:cycle_started",
    issueCount:       actionableIssues.length,
    projectId:        null,
    executionEnabled: SELF_HEALING_EXECUTION_ENABLED,
  });

  if (!issues?.length) {
    logger.debug("[Orchestrator] No issues detected");
    const report: SelfHealingCycleReport = {
      totalIssues:        0,
      resolved:           0,
      aiResolved:         0,
      failed:             0,
      skipped:            0,
      blocked:            0,
      proposed:           0,
      healthScore:        100,
      results:            [],
      durationMs:         Date.now() - cycleStart,
      completedAt:        Date.now(),
      executionSuspended: !SELF_HEALING_EXECUTION_ENABLED,
    };
    eventBus.emit({
      type:               "selfHealing:cycle_completed",
      totalIssues:        0,
      proposed:           0,
      blocked:            0,
      healthScore:        100,
      durationMs:         report.durationMs,
      executionSuspended: report.executionSuspended,
    });
    return report;
  }

  logger.debug(`[Orchestrator] ${actionableIssues.length} actionable issues found`);

  // ── Emit per-issue detection events ─────────────────────────────────────
  for (const issue of actionableIssues) {
    eventBus.emit({
      type:        "selfHealing:issue_detected",
      issueId:     issue.id,
      category:    issue.category,
      severity:    issue.severity,
      title:       issue.title,
      autoFixable: issue.autoFixable,
    });
  }

  // ── Announce suspension when execution is disabled ──────────────────────
  if (!SELF_HEALING_EXECUTION_ENABLED) {
    eventBus.emit({ type: "selfHealing:suspended", reason: SUSPENSION_REASON });
    logger.warn(
      `[Orchestrator] Execution suspended - ${actionableIssues.length} issue(s) will produce proposals only`
    );
  }

  const prioritized = prioritizeIssues(actionableIssues);
  const results: IssueHealingResult[] = [];

  // ── Main issue loop ──────────────────────────────────────────────────────
  // Routes to proposeRepair() when execution is suspended;
  // routes to healIssue() (with governance double-lock) when re-authorized.
  try {
    await withTimeout(
      (async () => {
        for (const issue of prioritized) {
          if (!issue?.id) continue;

          const result = !SELF_HEALING_EXECUTION_ENABLED
            ? await proposeRepair(issue)
            : await healIssue(issue, store);

          results.push(result);

          // Halt on unresolved critical issues (execution mode only)
          if (
            SELF_HEALING_EXECUTION_ENABLED &&
            result.status === "failed" &&
            issue.severity === "critical"
          ) {
            logger.error("[Orchestrator] Critical issue unresolved - halting cycle");
            break;
          }
        }
      })(),
      CYCLE_TIMEOUT,
    );
  } catch (err: any) {
    logger.error("[Orchestrator] Cycle timed out or crashed:", err?.message);
  }

  const resolved    = results.filter(r => r.status === "resolved").length;
  const aiResolved  = results.filter(r => r.status === "ai_resolved").length;
  const failed      = results.filter(r => r.status === "failed").length;
  const skipped     = results.filter(r => r.status === "skipped").length;
  const blocked     = results.filter(r => r.status === "blocked").length;
  const proposed    = results.filter(r => r.status === "proposed").length;
  const healthScore = computeHealthScore(results);
  const durationMs  = Date.now() - cycleStart;

  logger.info(
    `[Orchestrator] Cycle complete - ` +
    `proposed: ${proposed}, resolved: ${resolved + aiResolved}, ` +
    `failed: ${failed}, skipped: ${skipped}, score: ${healthScore}/100 (${durationMs}ms)` +
    (!SELF_HEALING_EXECUTION_ENABLED ? " [EXECUTION SUSPENDED]" : "")
  );

  // ── Emit cycle completion ─────────────────────────────────────────────────
  eventBus.emit({
    type:               "selfHealing:cycle_completed",
    totalIssues:        prioritized.length,
    proposed,
    blocked,
    healthScore,
    durationMs,
    executionSuspended: !SELF_HEALING_EXECUTION_ENABLED,
  });

  return {
    totalIssues:        prioritized.length,
    resolved,
    aiResolved,
    failed,
    skipped,
    blocked,
    proposed,
    healthScore,
    results,
    durationMs,
    completedAt:        Date.now(),
    executionSuspended: !SELF_HEALING_EXECUTION_ENABLED,
  };
}

/* =========================================================
   PUBLIC API
   ========================================================= */

export async function healSpecificIssue(
  issueId: string,
): Promise<IssueHealingResult | null> {
  const store  = getProjectState();
  const issues = await safeExecute(() => runRuntimeMonitor());
  const issue  = issues?.find(i => i.id === issueId);

  if (!issue) {
    logger.warn(`[Orchestrator] Issue ${issueId} not found in current monitor scan`);
    return null;
  }

  // Delegate to healIssue() which has its own governance double-lock
  return healIssue(issue, store);
}

export function getHealingStats(): {
  failures:  Record<string, number>;
  successes: Record<string, number>;
  blocked:   string[];
} {
  return {
    failures:  { ...failureMap },
    successes: { ...successMap },
    blocked:   Array.from(blockedSet),
  };
}

export function resetHealingMemory(): void {
  Object.keys(failureMap).forEach(k => delete failureMap[k]);
  Object.keys(successMap).forEach(k => delete successMap[k]);
  blockedSet.clear();
  logger.debug("[Orchestrator] Healing memory reset");
}
